'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

// Sanitize string to remove invalid characters that cause DOMException in some browsers
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    // Remove null bytes, control characters, and other problematic Unicode characters
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').replace(/[\x80-\x9F]/g, '').replace(/\uFFFE|\uFFFF/g, '');
  } catch {
    return '';
  }
}
interface GumletEmbedProps {
  embedJs: string;
}

// Skeleton component for Gumlet video loading state
const GumletSkeleton = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      paddingTop: "56.25%"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="animate-pulse p-4"><div className="relative w-full bg-gray-200 rounded-lg overflow-hidden" style={t0}><div className="absolute inset-0 flex items-center justify-center"><div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center"><div className="w-0 h-0 border-t-10 border-t-transparent border-b-10 border-b-transparent ml-2" style={{
              borderLeftWidth: "16px",
              borderLeftColor: "#9ca3af",
              borderTopWidth: "10px",
              borderBottomWidth: "10px"
            }} /></div></div></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export default function GumletEmbed({
  embedJs
}: GumletEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!embedJs || !containerRef.current) {
      setIsLoading(false);
      return;
    }
    try {
      // Decode the embed content - handle both Base64 and plain HTML
      let decodedContent: string;
      try {
        // Try Base64 decoding first
        decodedContent = atob(embedJs);
      } catch {
        // If atob fails, the content might be plain HTML or URL-encoded
        try {
          decodedContent = decodeURIComponent(embedJs);
        } catch {
          // Use as-is if all decoding fails
          decodedContent = embedJs;
        }
      }

      // Sanitize and set the embed content
      containerRef.current.innerHTML = sanitizeForDOM(decodedContent);

      // Look for script tags and execute them
      const scripts = containerRef.current.getElementsByTagName('script');
      let scriptsToLoad = 0;
      let scriptsLoaded = 0;
      const checkAllLoaded = () => {
        scriptsLoaded++;
        if (scriptsLoaded >= scriptsToLoad) {
          // Give extra time for video player to initialize
          setTimeout(() => setIsLoading(false), 800);
        }
      };

      // Count external scripts
      for (let i = 0; i < scripts.length; i++) {
        if (scripts[i].src) scriptsToLoad++;
      }
      if (scriptsToLoad === 0) {
        // No external scripts, hide skeleton after delay
        setTimeout(() => setIsLoading(false), 500);
      }

      // Execute scripts
      for (let i_0 = 0; i_0 < scripts.length; i_0++) {
        const script = scripts[i_0];
        if (script.src) {
          const newScript = document.createElement('script');
          newScript.src = script.src;
          newScript.async = true;
          if (script.charset) newScript.charset = script.charset;
          newScript.onload = () => {
            checkAllLoaded();
          };
          newScript.onerror = () => {
            console.error('GumletEmbed: Failed to load script', script.src);
            setError('Failed to load video');
            setIsLoading(false);
          };
          document.head.appendChild(newScript);
        } else if (script.textContent) {
          try {
            // Safer alternative to eval() - create a script element
            const inlineScript = document.createElement('script');
            inlineScript.textContent = script.textContent;
            document.body.appendChild(inlineScript);
            // Clean up after execution
            document.body.removeChild(inlineScript);
          } catch (err_0) {
            console.error('GumletEmbed: Error executing inline script', err_0);
          }
        }
      }

      // Also observe for iframe/video element appearing
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.addedNodes.length) {
            const hasMedia = containerRef.current?.querySelector('iframe, video');
            if (hasMedia) {
              setTimeout(() => setIsLoading(false), 300);
              observer.disconnect();
              break;
            }
          }
        }
      });
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true
      });

      // Fallback: hide skeleton after max wait time
      const fallbackTimer = setTimeout(() => {
        setIsLoading(false);
        observer.disconnect();
      }, 5000);
      return () => {
        observer.disconnect();
        clearTimeout(fallbackTimer);
      };
    } catch (err) {
      console.error('GumletEmbed: Error processing embed', err);
      setError('Failed to process video embed');
      setIsLoading(false);
    }
  }, [embedJs]);
  if (error) {
    return <div className="p-4 border border-red-300 bg-red-50 rounded m-4">
        <p className="text-red-600">Error loading video: {error}</p>
      </div>;
  }
  return <div className="relative">
      {isLoading && <GumletSkeleton />}
      <div ref={containerRef} className={isLoading ? 'absolute opacity-0 pointer-events-none' : ''} />
    </div>;
}